import { Match } from '@/types';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface MatchListItemProps {
    match: Match;
}

export function MatchListItem({ match }: MatchListItemProps) {
    const navigate = useNavigate();
    const isWin = match.result === 'Win';
    const isDraw = match.result === 'Draw';

    return (
        <div
            onClick={() => navigate(`/matches/${match.id}`)}
            className="group relative overflow-hidden bg-bg-elevated border border-white/5 rounded-xl p-4 active:scale-[0.98] transition-all duration-200 cursor-pointer hover:border-white/10"
        >
            <div className="flex items-center justify-between">
                {/* Left: Result Badge & Opponent */}
                <div className="flex items-center gap-4">
                    <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg shadow-inner",
                        isWin ? "bg-status-success/10 text-status-success" :
                            isDraw ? "bg-white/10 text-text-secondary" :
                                "bg-status-error/10 text-status-error"
                    )}>
                        {match.result.charAt(0)}
                    </div>

                    <div>
                        <h3 className="font-bold text-text-primary text-lg leading-none mb-1">
                            {match.opponentName}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-text-muted">
                            <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(parseISO(match.date), 'MMM d')}
                            </span>
                            {match.eventName && (
                                <>
                                    <span>•</span>
                                    <span>{match.eventName}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: Score & Method */}
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-xl font-black text-white leading-none">
                            {match.myScore}-{match.opponentScore}
                        </div>
                        <div className="text-[10px] uppercase font-bold text-text-muted tracking-wide mt-1">
                            {match.method}
                        </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-gold-base transition-colors" />
                </div>
            </div>

            {/* Win Gradient Glow */}
            {isWin && (
                <div className="absolute inset-0 bg-gradient-to-r from-status-success/5 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
        </div>
    );
}
